import type { FormatterType } from "@ext/markdown/core/edit/logic/Formatter/Formatters/typeFormats/getFormatterType";
import { Syntax } from "@ext/markdown/core/edit/logic/Formatter/Formatters/typeFormats/model/Syntax";
import type { NodeSerializerSpec } from "@ext/markdown/core/edit/logic/Prosemirror/to_markdown";
import { DEFAULT_FLOAT, format, hasEditorChanges } from "@ext/markdown/elements/image/render/logic/imageTransformer";
import type { Node } from "prosemirror-model";

const CLEAR_CROP = { x: 0, y: 0, w: 100, h: 100 };

/**
 * The size markup of an image: what it was drawn at, and the width it was dragged to. A picture
 * whose only mark is its size stays a Markdown image -- the tag form is for what Markdown cannot
 * say: a crop, a drawing on top, a float other than the one every image already has.
 */
const size = (node: Node) => {
	const parts: string[] = [];
	if (node.attrs?.width) parts.push(`width=${node.attrs.width}`);
	if (node.attrs?.height) parts.push(`height=${node.attrs.height}`);
	if (node.attrs?.scale) parts.push(`scale=${node.attrs.scale}`);
	return parts.length ? `{${parts.join(" ")}}` : "";
};

const imageNodeFormatter =
	(formatter: FormatterType): NodeSerializerSpec =>
	(state, node) => {
		const newFormat =
			hasEditorChanges(node.attrs?.crop, node.attrs?.objects) ||
			(node.attrs?.float && node.attrs.float !== DEFAULT_FLOAT);
		if (newFormat) {
			const attrs = {
				src: node.attrs.src,
				alt: node.attrs.alt,
				title: node.attrs.title,
				...format(
					node.attrs?.width,
					node.attrs?.height,
					node.attrs?.crop ?? CLEAR_CROP,
					node.attrs?.objects ?? [],
					node.attrs?.scale,
					node.attrs?.float,
				),
				href: node.attrs.href,
			};
			state.write(formatter.openTag(formatter.type === Syntax.github ? "img" : "image", attrs, true));
		} else {
			const src = node.attrs.src?.includes?.(" ") ? `<${node.attrs.src}>` : node.attrs.src;
			const title = node.attrs.title ? ` "${node.attrs.title.replace(/"/g, '\\"')}"` : "";
			const image = `![${state.esc(node.attrs.alt || "")}](${src}${title})${size(node)}`;
			const href = node.attrs.href?.includes?.(" ") ? `<${node.attrs.href}>` : node.attrs.href;
			state.write(href ? `[${image}](${href})` : image);
		}
		state.closeBlock(node);
	};

export default imageNodeFormatter;
